/**
 * @file teamStore.ts
 * @fileoverview Beheert de spelers- en coachroosters in Zustand.
 * Het bevat acties om spelers toe te voegen, bij te werken of te verwijderen
 * en wijzigingen te synchroniseren met de backend-tabellen.
 */
import { create } from 'zustand';
import { Player, Position, Coach, PlayerStatistics } from '../types';
import { createPlayerApi, deletePlayerApi, getPlayerApi, updatePlayerApi } from '../lib/playersApi';
import { getTeamApi, updateTeamApi } from '../lib/teamsApi';
import { normalizePositionPreferences, serializePositionPreferenceSets, withNormalizedPositionPreferences } from '../utils/player';
import { decryptIfEncrypted } from '../utils/encryption';

// Store for team data including players and coaches. Provides helpers to
// sync players with the backend and manage them locally.

/**
 * Interface voor de state van het team.
 */
interface TeamState {
  /** De lijst met spelers in het team. */
  players: Player[];
  /** De lijst met coaches van het team. */
  coaches: Coach[];
  /** Voegt een speler toe aan het team. */
  addPlayer: (teamId: string, player: Omit<Player, 'id'>) => Promise<void>;
  /** Werkt de gegevens van een speler bij. */
  updatePlayer: (playerId: string, updates: Partial<Player>) => Promise<void>;
  /** Verwijdert een speler uit het team. */
  removePlayer: (teamId: string, playerId: string) => Promise<void>;
  /** Voegt een coach toe aan het team. */
  addCoach: (coach: Coach) => void;
  /** Werkt de gegevens van een coach bij. */
  updateCoach: (id: string, updates: Partial<Coach>) => void;
  /** Verwijdert een coach uit het team. */
  removeCoach: (id: string) => void;
  /** Haalt spelers op basis van hun positie. */
  getPlayersByPosition: (position: Position) => Player[];
  /** Synchroniseert de spelerslijst met de database. */
  syncPlayers: (teamId: string) => Promise<void>;
  /** Herstelt de store naar de initiële staat. */
  reset: () => void;
}

const getInitialState = () => ({
  players: [] as Player[],
  coaches: [] as Coach[],
});

type TeamPlayerRecord = Record<string, unknown>;

const mapPlayerRecord = async (record: TeamPlayerRecord): Promise<Player> => {
  const normalizedPositions = normalizePositionPreferences(
    record.positionPreferencesByVariant && typeof record.positionPreferencesByVariant === 'object'
      ? {
          ...(record.positionPreferencesByVariant as Record<string, unknown>),
          variant: record.positionPreferenceVariant,
        }
      : record.positions ?? record.positionPreferences
  );
  const clubId = typeof record.club_id === 'string' ? record.club_id : '';
  let name = typeof record.name === 'string' ? record.name : '';
  if (name && clubId) {
    const decryptedName = await decryptIfEncrypted({ value: name, clubId });
    name = typeof decryptedName === 'string' && decryptedName.length > 0 ? decryptedName : name;
  }

  return {
    id: String(record.id),
    clubId,
    name,
    number: String(record.number ?? ''),
    positionPreferences: normalizedPositions.preferences,
    positionPreferenceVariant: normalizedPositions.variant,
    positionPreferencesByVariant: normalizedPositions.variants,
    hasPositionPriority: normalizedPositions.hasExplicitPriorities,
    gender: record.gender === 'male' || record.gender === 'female' ? record.gender : null,
    isGoalkeeper: Boolean(record.isGoalkeeper),
    condition: typeof record.condition === 'number' ? record.condition : undefined,
    performance: typeof record.performance === 'number' ? record.performance : undefined,
    playingTime: typeof record.playing_time === 'number' ? record.playing_time : undefined,
    goals: typeof record.goals === 'number' ? record.goals : undefined,
    evaluations: Array.isArray(record.evaluations) ? record.evaluations : [],
    statistics: {
      totalGoals: (record.statistics as PlayerStatistics | undefined)?.totalGoals ?? 0,
      totalPlayingTime: (record.statistics as PlayerStatistics | undefined)?.totalPlayingTime ?? 0,
      ...((record.statistics as Record<string, number>) || {}),
    },
  };
};

const extractTeamPlayerIds = (team: unknown): string[] => {
  if (!team || typeof team !== 'object' || !('players' in (team as Record<string, unknown>))) {
    return [];
  }

  const players = (team as Record<string, unknown>).players;
  if (!Array.isArray(players)) return [];

  return players
    .map((entry) => {
      if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
      if (entry && typeof entry === 'object' && 'id' in (entry as Record<string, unknown>)) {
        const id = (entry as Record<string, unknown>).id;
        return typeof id === 'string' || typeof id === 'number' ? String(id) : '';
      }
      return '';
    })
    .filter((id) => id.length > 0);
};

export const useTeamStore = create<TeamState>((set, get) => ({
  ...getInitialState(),

  addPlayer: async (teamId, player) => {
    try {
      const normalizedPlayer = withNormalizedPositionPreferences(player);
      const preferenceSets = serializePositionPreferenceSets(normalizedPlayer);

      const newPlayerData = await createPlayerApi({
        club_id: player.clubId,
        name: player.name,
        number: player.number ? parseInt(player.number, 10) : null,
        positions: preferenceSets,
        gender: player.gender,
        isGoalkeeper: player.isGoalkeeper,
        performance: player.performance,
        playing_time: player.playingTime,
        goals: player.goals,
        evaluations: player.evaluations,
        statistics: player.statistics,
        condition: player.condition,
      }, { teamId });

      if (!newPlayerData || typeof newPlayerData !== 'object' || !('id' in (newPlayerData as Record<string, unknown>))) {
        throw new Error('Failed to create player');
      }

      const team = await getTeamApi(teamId);
      const updatedPlayers = [...new Set([...extractTeamPlayerIds(team), String((newPlayerData as Record<string, unknown>).id)])];

      await updateTeamApi(teamId, { players: updatedPlayers });

      const savedPlayer = await mapPlayerRecord(newPlayerData as TeamPlayerRecord);
      set((state) => ({
        players: [
          ...state.players.filter(existing => existing.id !== savedPlayer.id),
          savedPlayer,
        ],
      }));
    } catch (error) {
      throw error;
    }
  },
  
  updatePlayer: async (playerId, updates) => {
    try {
      const merged = withNormalizedPositionPreferences({
        ...get().players.find(p => p.id === playerId),
        ...updates,
      } as Player);
      const preferenceSets = serializePositionPreferenceSets(merged);

      await updatePlayerApi(playerId, {
        club_id: updates.clubId,
        name: updates.name,
        number: updates.number ? parseInt(updates.number, 10) : null,
        positions: preferenceSets,
        gender: updates.gender,
        isGoalkeeper: updates.isGoalkeeper,
        performance: updates.performance,
        playing_time: updates.playingTime,
        goals: updates.goals,
        evaluations: updates.evaluations,
        statistics: updates.statistics,
        condition: updates.condition,
      });

      set((state) => ({
        players: state.players.map((player: Player) =>
          player.id === playerId
            ? {
                ...player,
                ...merged,
                hasPositionPriority: updates.hasPositionPriority ?? player.hasPositionPriority,
              }
            : player
        ),
      }));
    } catch (error) {
      throw error;
    }
  },
  
  removePlayer: async (teamId, playerId) => {
    try {
      const team = await getTeamApi(teamId);
      const updatedPlayers = extractTeamPlayerIds(team).filter((id) => id !== playerId);

      await updateTeamApi(teamId, { players: updatedPlayers });
      await deletePlayerApi(playerId);

      set((state) => ({
        players: state.players.filter((player: Player) => player.id !== playerId),
      }));
    } catch (error) {
      throw error;
    }
  },

  addCoach: (coach) => {
    set((state) => ({
      coaches: [...state.coaches, coach]
    }));
  },

  updateCoach: (id, updates) => {
    set((state) => ({
      coaches: state.coaches.map(coach => 
        coach.id === id ? { ...coach, ...updates } : coach
      )
    }));
  },

  removeCoach: (id) => {
    set((state) => ({
      coaches: state.coaches.filter(coach => coach.id !== id)
    }));
  },
  
  getPlayersByPosition: (position) => {
    return get()
      .players
      .filter((player: Player) =>
        player.positionPreferences.some(pp => pp.position === position)
      )
      .sort((a, b) => {
        const aPriority =
          a.positionPreferences.find(pp => pp.position === position)?.priority ?? Infinity;
        const bPriority =
          b.positionPreferences.find(pp => pp.position === position)?.priority ?? Infinity;
        return aPriority - bPriority;
      });
  },

  syncPlayers: async (teamId) => {
    try {
      const team = await getTeamApi(teamId);
      if (!team) throw new Error('Team not found');
      const teamRecord = team as Record<string, unknown>;
      const teamPlayers = Array.isArray(teamRecord.players) ? teamRecord.players : [];

      // If players is already an array of Player objects, use them directly
      if (
        teamPlayers.length > 0 &&
        typeof teamPlayers[0] === 'object'
      ) {
        const mapped = await Promise.all((teamPlayers as TeamPlayerRecord[]).map(mapPlayerRecord));
        set({ players: mapped });
        return;
      }

      const playerIds = extractTeamPlayerIds(team);
      if (playerIds.length === 0) {
        set({ players: [] });
        return;
      }

      const fetchedPlayers = await Promise.all(playerIds.map(async (id) => await getPlayerApi(id)));
      const mapped = await Promise.all(fetchedPlayers
        .filter((player): player is TeamPlayerRecord => Boolean(player && typeof player === 'object'))
        .map((player) => mapPlayerRecord(player)));
      set({ players: mapped });
    } catch (error) {
      throw error;
    }
  },

  reset: () => {
    set(getInitialState());
  }
}));
